import React from "react";

import { Picto, Spinner, Title } from "@amphore-dev/amphore-lib";
import { CalendarOptions, EventInput } from "@fullcalendar/core";
import { DateRange, ViewOptionsRefined } from "@fullcalendar/core/internal";
import frLocale from "@fullcalendar/core/locales/fr";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import { t } from "i18next";
import { useTranslation } from "react-i18next";

import { cn } from "@/utils/cn";

import "./Calendar.css";

export interface ICalendarProps extends CalendarOptions {
	tasks?: EventInput[];
	isLoading?: boolean;
	onNavigate?: (action: TNavigateAction, range: DateRange) => void;
	calendarRef?: React.RefObject<FullCalendar | null>;
	/** Narrow screens: short weekdays, fixed-height cells. */
	compact?: boolean;
}

const CalendarInner: React.FC<ICalendarProps> = ({
	tasks = [],
	isLoading,
	onNavigate,
	calendarRef,
	compact = false,
	...props
}) => {
	const [ActiveView, setActiveView] = React.useState<string>("dayGridMonth");
	const ownRef = React.useRef<FullCalendar | null>(null);
	const innerRef = calendarRef ?? ownRef;
	// Re-renders on a language change, which reading localStorage directly
	// never did: the calendar kept the language it was mounted with.
	const { i18n } = useTranslation();
	// "fr-FR" -> "fr", which is what the loaded FullCalendar locale answers
	// to; English is FullCalendar's built-in default.
	const locale = (i18n.language ?? "en").slice(0, 2);

	return (
		<div
			className={cn([
				"calendar flex flex-col gap-4",
				compact && "calendar--compact",
			])}
		>
			<CalendarToolbar
				isLoading={isLoading}
				calendarRef={innerRef}
				onNavigate={onNavigate}
				views={props.views}
				onViewChange={(view) => setActiveView(view)}
			/>

			<FullCalendar
				ref={innerRef}
				events={tasks}
				plugins={[dayGridPlugin, interactionPlugin]}
				initialView={ActiveView}
				firstDay={1}
				height={"auto"}
				locales={[frLocale]}
				locale={locale}
				headerToolbar={false}
				dayHeaderFormat={{ weekday: compact ? "short" : "long" }}
				{...props}
			/>
		</div>
	);
};

export const Calendar = React.memo(CalendarInner);

type TNavigateAction = "prev" | "today" | "next";

interface ICalendarToolbarProps extends CalendarOptions {
	isLoading?: boolean;
	label?: string;
	onViewChange?: (view: string) => void;
	onNavigate?: (action: TNavigateAction, range: DateRange) => void;
	calendarRef: React.RefObject<FullCalendar | null>;
}

const CalendarToolbar: React.FC<ICalendarToolbarProps> = ({
	label = "Calendar",
	onNavigate = () => {},
	views = [] as unknown as { [viewId: string]: ViewOptionsRefined },
	isLoading = false,
	calendarRef,
}) => {
	const [, setLastUpdate] = React.useState<Date | null>(null); // Used to force re-render

	const calContext = calendarRef?.current?.getApi();

	const getViews = () => {
		if (Array.isArray(views)) {
			return views;
		}

		return Object.keys(views);
	};

	const genViewsButtons = () => {
		const viewsKeys = getViews();

		if (viewsKeys?.length < 2) return null;

		return viewsKeys.map((viewKey) => {
			const view = views[viewKey];

			return (
				<button
					key={viewKey}
					onClick={() => {
						if (calContext) {
							calContext.changeView(viewKey);
							// onViewChange(view);
						}
					}}
					className="capitalize"
					disabled={isLoading}
				>
					{view.label || viewKey}
				</button>
			);
		});
	};

	const handleNavigate = (action: TNavigateAction) => {
		if (!calContext) return;

		calContext[action.toLowerCase() as TNavigateAction]();

		onNavigate(action, {
			start: calContext.view.currentStart,
			end: calContext.view.currentEnd,
		});
		setLastUpdate(new Date());
	};

	return (
		<div className="calendar-toolbar flex flex-row items-center justify-between px-2">
			<div className="flex items-center gap-4">
				<div className="calendar-btn-group text-md flex items-center *:h-6">
					<button
						onClick={() => handleNavigate("prev")}
						title={t("dates.previousMonth")}
					>
						<Picto icon="chevron" className="h-4 w-4 rotate-180" />
					</button>
					<button
						onClick={() => handleNavigate("today")}
						title={t("dates.today")}
					>
						<Picto icon="calendar" className="h-4 w-4" />
					</button>
					<button
						onClick={() => handleNavigate("next")}
						title={t("dates.nextMonth")}
					>
						<Picto icon="chevron" className="h-4 w-4" />
					</button>
				</div>
				<Title size="h4" className="capitalize">
					{calContext?.view.title || label}
				</Title>
				{isLoading && <Spinner size="lg" />}
			</div>
			<div className="calendar-btn-group">{genViewsButtons()}</div>
		</div>
	);
};
